import { createFileRoute, Link, notFound, useNavigate, useRouterState, useSearch } from "@tanstack/react-router";
import { useCallback } from "react";
import { setSeoPageCache } from "@/lib/page-cache";
import { Navbar } from "@/components/landing/Navbar";
import { Reveal } from "@/components/Reveal";
import { SeoFaq } from "@/components/seo/SeoFaq";
import { Skeleton } from "@/components/ui/skeleton";
import {
  breadcrumbListSchema,
  collectionPageSchema,
  faqPageSchema,
  jsonLdScript,
} from "@/lib/seo-schema";
import { US_CITIES_VALID } from "@/data/us-cities";
import { specialtyFromSlug, specialtySlug, stateFromNameSlug, stateNameSlug, toSlug } from "@/lib/seo-slugs";
import { resolveDbSpecialtySlug } from "@/hooks/useAllSpecialties";
import { runProviderSearch, cleanQueryText, type SearchLoaderResult } from "@/lib/search-loader";
import { SearchExperience, type ResolvedCity } from "@/components/search/SearchExperience";
import { providersRouteFor } from "@/lib/providers-nav";

const SITE = "https://heallexa.com";

// The most specific page in the /providers hierarchy — state, city, AND
// specialty all baked into the path (matching the reference site's
// continuous "/attorneys/{state}/{city}/{specialty}" shape, no query
// string). No collision risk: 3 segments, distinct from /providers/$id (1)
// and /providers/$state/$city (2).
type ProviderCitySpecialtySearch = { insurance?: string; q?: string; radius?: number; page?: number };

type StateInfo = { code: string; name: string };
type CityInfo = { name: string; state: string };

function PageSkeleton() {
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="mx-auto max-w-[1200px] px-6 pt-32 md:pt-24 pb-16">
        <Skeleton className="h-4 w-48 mb-6" />
        <Skeleton className="h-12 w-2/3 mb-4" />
        <Skeleton className="h-4 w-full max-w-lg mb-6" />
      </main>
    </div>
  );
}

// NOTE: the trailing underscore on $city_ is a TanStack Router file-based
// routing escape — without it, this file (providers.$state.$city.$specialty)
// would be treated as a CHILD of providers.$state.$city.tsx (which already
// defines a full-page component with no <Outlet/>), so this route would
// never actually render. The underscore keeps the URL itself clean
// (/providers/{state}/{city}/{specialty}, no underscore) while making this
// a flat, independent route under root.
export const Route = createFileRoute("/providers/$state/$city_/$specialty")({
  validateSearch: (s: Record<string, unknown>): ProviderCitySpecialtySearch => ({
    insurance: cleanQueryText(s.insurance),
    q: cleanQueryText(s.q),
    radius: typeof s.radius === "number" ? s.radius : (typeof s.radius === "string" ? Number(s.radius) || undefined : undefined),
    page: typeof s.page === "number" ? s.page : (typeof s.page === "string" ? Number(s.page) || undefined : undefined),
  }),
  loaderDeps: ({ search }) => search,
  loader: async ({ params, deps }) => {
    await setSeoPageCache();
    const state = stateFromNameSlug(params.state);
    if (!state) throw notFound();
    const citySlug = params.city.toLowerCase();
    const city = US_CITIES_VALID.find((c) => c.state === state.code && toSlug(c.name) === citySlug);
    if (!city) throw notFound();
    const specialty = specialtyFromSlug(params.specialty) ?? (await resolveDbSpecialtySlug(params.specialty));
    if (!specialty) throw notFound();
    const searchResult = await runProviderSearch({ ...deps, specialty, location: `${city.name}, ${state.code}` });
    return { state, city, specialty, searchResult };
  },
  pendingMs: 100,
  pendingMinMs: 300,
  pendingComponent: PageSkeleton,
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const { state, city, specialty } = loaderData;
    const title = `${specialty} in ${city.name}, ${state.code} | Heallexa`;
    const description = `Find ${specialty.toLowerCase()} in ${city.name}, ${state.code}. Compare providers, insurance accepted, and patient reviews on Heallexa.`;
    const stateSlug = stateNameSlug(state.name);
    const url = `${SITE}/providers/${stateSlug}/${toSlug(city.name)}/${specialtySlug(specialty)}`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        jsonLdScript(collectionPageSchema({ name: title, description, url })),
        jsonLdScript(
          breadcrumbListSchema([
            { name: "Home", url: "/" },
            { name: "Providers", url: "/browse" },
            { name: state.name, url: `/providers/${stateSlug}` },
            { name: city.name, url: `/providers/${stateSlug}/${toSlug(city.name)}` },
            { name: specialty, url },
          ]),
        ),
        jsonLdScript(
          faqPageSchema([
            {
              q: `How do I find a ${specialty.toLowerCase()} in ${city.name}?`,
              a: `Use Heallexa to browse ${specialty.toLowerCase()} in ${city.name}, filter by insurance, and contact the office directly.`,
            },
            {
              q: `Is Heallexa free for patients?`,
              a: `Yes. Search, browse, and contact ${city.name} providers for free, no signup required.`,
            },
          ]),
        ),
      ],
    };
  },
  component: CitySpecialtyPage,
  notFoundComponent: () => (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <h1 className="font-display text-2xl font-semibold mb-2">Page not found</h1>
        <Link to="/browse" className="text-primary underline">
          Browse all providers
        </Link>
      </div>
    </div>
  ),
});

function BelowResultsContent({ state, city, specialty }: { state: StateInfo; city: CityInfo; specialty: string }) {
  const stateSlug = stateNameSlug(state.name);
  const lower = specialty.toLowerCase();
  return (
    <div className="mx-auto max-w-[1200px] px-6 py-6">
      <Reveal from="up">
        <section className="mb-12 card-surface rounded-md p-6 sm:p-8">
          <h2 className="font-display text-[clamp(1.4rem,2.4vw,1.8rem)] font-semibold tracking-[-0.03em] leading-[1.15] text-foreground mb-3">
            {specialty} in {city.name}
          </h2>
          <p className="text-[14px] leading-relaxed text-muted-foreground">
            Compare {lower} in {city.name}, {state.code} by insurance accepted, rating, and availability. Browse{" "}
            <Link to="/providers/$id" params={{ id: specialtySlug(specialty) }} className="text-primary hover:underline">
              {lower} nationwide
            </Link>{" "}
            or{" "}
            <Link to="/providers/$state/$city" params={{ state: stateSlug, city: toSlug(city.name) }} className="text-primary hover:underline">
              all providers in {city.name}
            </Link>.
          </p>
        </section>
      </Reveal>
      <SeoFaq
        className="mb-8"
        items={[
          {
            q: `How do I find a ${lower} in ${city.name}?`,
            a: `Use Heallexa to browse ${lower} in ${city.name}, filter by insurance, and contact the office directly.`,
          },
          {
            q: `Is Heallexa free for patients?`,
            a: `Yes. Search, browse, and contact ${city.name} providers for free, no signup required.`,
          },
        ]}
      />
    </div>
  );
}

function CitySpecialtyPage() {
  const { state, city, specialty, searchResult }: { state: StateInfo; city: CityInfo; specialty: string; searchResult: SearchLoaderResult } = Route.useLoaderData();
  const search = useSearch({ from: "/providers/$state/$city_/$specialty" });
  const navigate = useNavigate({ from: "/providers/$state/$city/$specialty" });
  const isRouteLoading = useRouterState({
    select: (s) => s.matches.some((m) => m.routeId === "/providers/$state/$city_/$specialty" && m.status === "pending"),
  });

  const onQueryChange = useCallback((q: string) => {
    navigate({ search: (prev: any) => ({ ...prev, q: q || undefined, page: undefined }), replace: true });
  }, [navigate]);

  const onSpecialtyCommit = useCallback((newSpecialty: string) => {
    const dest = providersRouteFor(newSpecialty, { city: city.name, state: state.code });
    if (dest) navigate({ to: dest.to, params: dest.params as any, search: { page: undefined } } as any);
  }, [navigate, city.name, state.code]);

  const onCityCommit = useCallback((cityLabel: string, matched: ResolvedCity | null) => {
    const dest = matched ? providersRouteFor(specialty, matched) : null;
    if (dest) {
      navigate({ to: dest.to, params: dest.params as any, search: { page: undefined } } as any);
    } else {
      navigate({ to: "/search", search: { location: cityLabel || undefined, specialty } });
    }
  }, [navigate, specialty]);

  const onRadiusChange = useCallback((radius: number) => {
    navigate({ search: (prev: any) => ({ ...prev, radius: radius > 0 ? radius : undefined, page: undefined }), replace: true });
  }, [navigate]);

  const onPageChange = useCallback((page: number) => {
    navigate({ search: (prev: any) => ({ ...prev, page: page === 1 ? undefined : page }), replace: false });
  }, [navigate]);

  return (
    <SearchExperience
      loaderData={searchResult}
      initial={{ q: search.q ?? "", location: `${city.name}, ${state.code}`, radius: search.radius ?? 0 }}
      isRouteLoading={isRouteLoading}
      onQueryChange={onQueryChange}
      onLocationSubmit={(label) => onCityCommit(label, null)}
      onSpecialtyCommit={onSpecialtyCommit}
      onCityCommit={onCityCommit}
      onRadiusChange={onRadiusChange}
      onPageChange={onPageChange}
      belowResults={<BelowResultsContent state={state} city={city} specialty={specialty} />}
    />
  );
}
